'use client'

import { useRef } from 'react'
import { usePathname } from 'next/navigation'
import { motion } from 'framer-motion'
import { ArrowRight, ArrowUp, Calendar, ChevronRight, Loader2, PackageOpen, Truck } from 'lucide-react'
import moment from 'moment'
import { useInfiniteProducts } from '@/hooks/use-infinite-products'
import { useInfiniteScroll } from '@/hooks/use-infinite-scroll'
import { useCountdown } from '@/hooks/use-countdown'
import { useProductsByIds, useCategoryExpander } from '@/lib/commerce/hooks'
import { formatImageURLGoogle } from '@/utils/util'
import { getCampaignDiscountInfo, getCampaignTargetInfo, getCampaignStatus } from '@/utils/campaign'
import { navigateUrl } from '@/utils/client-request'
import ProductCard from '../product/ProductCard'

export default function Campaign({ vendor, campaign }: { vendor: any, campaign: any, wishlist?: any }) {
  const pathname = usePathname()
  const targetInfo = getCampaignTargetInfo(campaign.targets)
  const discountInfo = getCampaignDiscountInfo(campaign.targets)
  const status = getCampaignStatus(campaign.startDate, campaign.endDate)
  const countdownEnabled = status === 'active'
  const countdown = useCountdown(campaign.endDate, countdownEnabled)

  const statusPill = (() => {
    if (status === 'active') {
      return (
        <span
          className="inline-flex items-center gap-2 px-2.5 h-7 rounded-full text-[10.5px] font-semibold uppercase tracking-[0.18em]"
          style={{ background: 'rgba(16,185,129,0.16)', color: '#34d399', border: '1px solid rgba(16,185,129,0.3)' }}
        >
          <span className="inline-flex w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
          Идэвхтэй
        </span>
      )
    }
    if (status === 'expired') {
      return (
        <span
          className="inline-flex items-center gap-2 px-2.5 h-7 rounded-full text-[10.5px] font-semibold uppercase tracking-[0.18em]"
          style={{ background: 'rgba(244,63,94,0.16)', color: '#fda4af', border: '1px solid rgba(244,63,94,0.3)' }}
        >
          Дууссан
        </span>
      )
    }
    return (
      <span
        className="inline-flex items-center gap-2 px-2.5 h-7 rounded-full text-[10.5px] font-semibold uppercase tracking-[0.18em]"
        style={{ background: 'rgba(255,255,255,0.035)', color: 'rgba(255,255,255,0.55)', border: '1px solid rgba(255,255,255,0.13)' }}
      >
        Удахгүй
      </span>
    )
  })()

  return (
    <div>
      <div className="cont pt-8 pb-3 flex flex-wrap items-center gap-1.5 text-[12px]" style={{ color: 'rgba(255,255,255,0.55)' }}>
        <a href={navigateUrl('/', pathname)} className="hover:text-white transition-colors">Эх нүүр</a>
        <ChevronRight className="h-3 w-3" style={{ opacity: 0.4 }} />
        <a href={navigateUrl('/c', pathname)} className="hover:text-white transition-colors">Хямдралын аян</a>
        <ChevronRight className="h-3 w-3" style={{ opacity: 0.4 }} />
        <span className="text-white truncate">{campaign.name}</span>
      </div>

      <div className="cont pt-3 pb-8">
        {campaign.campaignBanner ? (
          <a
            href="#products"
            className="block relative aspect-4/5 sm:aspect-16/9 md:aspect-16/7 rounded-2xl sm:rounded-3xl overflow-hidden group"
            style={{ background: 'rgba(255,255,255,0.035)', border: '1px solid rgba(255,255,255,0.13)' }}
          >
            <img
              src={formatImageURLGoogle(campaign.campaignBanner)}
              alt={campaign.name}
              className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.02]"
            />
            <div className="absolute inset-0 sm:hidden" style={{ background: 'linear-gradient(180deg, rgba(0,0,0,0.25) 25%, rgba(0,0,0,0.85) 100%)' }} />
            <div className="absolute inset-0 hidden sm:block" style={{ background: 'linear-gradient(110deg, rgba(0,0,0,0.7) 30%, rgba(0,0,0,0.1) 75%)' }} />
            <div className="relative h-full p-5 sm:p-10 md:p-14 flex flex-col justify-between">
              <div className="flex items-center gap-2 flex-wrap">
                {statusPill}
                <span className="text-[10.5px] font-semibold uppercase tracking-[0.22em] text-white" style={{ opacity: 0.8 }}>
                  Хямдралын аян
                </span>
              </div>
              <div>
                <h1
                  className="font-semibold tracking-[-0.03em] leading-[1.02] sm:leading-[0.95] text-white max-w-3xl line-clamp-3"
                  style={{ fontFamily: 'Gilroy, Inter, sans-serif', fontSize: 'clamp(26px, 6vw, 72px)' }}
                >
                  {campaign.name}
                </h1>
                {discountInfo && (
                  <div className="mt-3 sm:mt-4 inline-flex items-center gap-2 text-[14px] font-semibold tracking-[-0.01em] text-white">
                    <span
                      className="px-2 py-0.5 rounded text-[11px] sm:text-[12px] uppercase tracking-[0.18em]"
                      style={{ background: 'white', color: '#0a0a0c' }}
                    >
                      {discountInfo.label} {discountInfo.suffix}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </a>
        ) : (
          <div className="text-[10px] font-semibold uppercase tracking-[0.22em]" style={{ color: '#a8a8b3' }}>Хямдралын аян</div>
        )}

        {!campaign.campaignBanner && (
          <h1
            className="mt-3 font-semibold tracking-[-0.03em] leading-[0.95] text-white max-w-3xl"
            style={{ fontFamily: 'Gilroy, Inter, sans-serif', fontSize: 'clamp(32px, 6vw, 72px)' }}
          >
            {campaign.name}
          </h1>
        )}
      </div>

      <div className="cont pb-8">
        <div
          className="grid items-center gap-x-8 gap-y-6 py-7 px-2 grid-cols-1 lg:grid-cols-[1fr_auto_auto_auto]"
          style={{ borderTop: '1px solid rgba(255,255,255,0.07)', borderBottom: '1px solid rgba(255,255,255,0.07)' }}
        >
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-[0.22em]" style={{ color: '#a8a8b3' }}>
              Аяны хугацаа
            </div>
            <div className="mt-2 inline-flex items-center gap-2 text-[14px] tabular-nums" style={{ color: 'rgba(255,255,255,0.55)' }}>
              <Calendar className="h-3.5 w-3.5" />
              {campaign.startDate ? (
                <>
                  <span className="text-white">{moment(campaign.startDate).format('YYYY · MM.DD')}</span>
                  <span style={{ opacity: 0.4 }}>—</span>
                  <span className="text-white">{campaign.endDate ? moment(campaign.endDate).format('MM.DD') : '∞'}</span>
                  {campaign.endDate && campaign.startDate && (
                    <span className="ml-3 text-[12px]" style={{ color: 'rgba(255,255,255,0.4)' }}>
                      · {moment(campaign.endDate).diff(moment(campaign.startDate), 'days')} өдөр
                    </span>
                  )}
                </>
              ) : (
                <span className="text-white">Хязгааргүй</span>
              )}
            </div>
          </div>

          {countdown && (
            <>
              <div className="hidden lg:block w-px h-14 self-center" style={{ background: 'rgba(255,255,255,0.07)' }} />
              <CountdownCells countdown={countdown} />
            </>
          )}

          <div className="flex items-center gap-2 flex-wrap lg:ml-auto">
            {campaign.type === 'freeShipping' && (
              <span
                className="inline-flex items-center gap-1.5 px-3 h-9 rounded-full text-[12.5px] font-semibold"
                style={{ background: 'rgba(16,185,129,0.16)', color: '#34d399', border: '1px solid rgba(16,185,129,0.3)' }}
              >
                <Truck className="h-3.5 w-3.5" />
                Үнэгүй хүргэлт
              </span>
            )}
            <a
              href="#products"
              className="h-12 px-6 rounded-full text-[13.5px] font-semibold inline-flex items-center gap-2 transition-opacity hover:opacity-90"
              style={{ background: 'white', color: '#0a0a0c' }}
            >
              Бүтээгдэхүүн харах <ArrowRight className="h-3.5 w-3.5" />
            </a>
          </div>
        </div>

        {campaign.description && (
          <p className="mt-6 text-[15px] leading-7 max-w-3xl" style={{ color: 'rgba(255,255,255,0.6)' }}>
            {campaign.description}
          </p>
        )}
      </div>

      <div id="products">
        {targetInfo.type === 'all' ? (
          <InfiniteCampaignGrid vendorId={vendor?.id} vendor={vendor} />
        ) : targetInfo.type === 'category' ? (
          <CategoryCampaignGrid vendorId={vendor?.id} categoryIds={targetInfo.categoryIds} vendor={vendor} />
        ) : targetInfo.type === 'product' ? (
          <ProductIdsCampaignGrid vendorId={vendor?.id} productIds={targetInfo.productIds} vendor={vendor} />
        ) : null}
      </div>
    </div>
  )
}

function pad2(n: number) { return String(n).padStart(2, '0') }

function CountdownCells({ countdown }: { countdown: { d: number, h: number, m: number, s: number } }) {
  const cells = [
    { v: countdown.d, k: 'өдөр' },
    { v: countdown.h, k: 'цаг' },
    { v: countdown.m, k: 'мин' },
    { v: countdown.s, k: 'сек' },
  ]
  return (
    <div className="flex flex-col">
      <div className="text-[9.5px] font-semibold tracking-[0.22em] uppercase mb-2" style={{ color: '#a8a8b3' }}>
        Аян дуусахад
      </div>
      <div className="flex items-end gap-2">
        {cells.map((c, i) => (
          <div key={c.k} className="flex items-end gap-2">
            <div className="flex flex-col items-center">
              <div
                className="rounded-lg font-bold tabular-nums tracking-[-0.02em] grid place-items-center min-w-12 px-2.5 py-2 text-[20px] text-white"
                style={{
                  background: 'rgba(255,255,255,0.06)',
                  border: '1px solid rgba(255,255,255,0.13)',
                  fontFamily: 'Gilroy, Inter, sans-serif',
                }}
              >
                {pad2(c.v)}
              </div>
              <div className="mt-1.5 text-[9px] uppercase tracking-[0.18em] font-semibold" style={{ color: 'rgba(255,255,255,0.55)' }}>
                {c.k}
              </div>
            </div>
            {i < cells.length - 1 && (
              <div className="pb-6 text-[18px] font-bold" style={{ color: 'rgba(255,255,255,0.4)' }}>:</div>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}

function CampaignProductGrid({
  products, isLoading, isLoadingMore, total, hasMore, loadMore, vendor, skeletonCount,
}: {
  products: any[], isLoading: boolean, isLoadingMore?: boolean, total: number, hasMore?: boolean,
  loadMore?: () => void, vendor: any, skeletonCount: number,
}) {
  const loadMoreRef = useRef<HTMLDivElement>(null)
  useInfiniteScroll({
    targetRef: loadMoreRef,
    onLoadMore: () => loadMore?.(),
    enabled: !!hasMore && !!loadMore && !isLoading && !isLoadingMore,
  })

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: 'smooth' })
  const visibleCount = products.length

  return (
    <motion.div
      initial={{ opacity: 0, y: -10 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ delay: 0, duration: 0.5, ease: 'easeIn' }}
      viewport={{ once: true }}
      className="cont pb-16"
    >
      <div className="flex items-end justify-between gap-4 flex-wrap mb-7">
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-[0.22em] mb-2" style={{ color: '#a8a8b3' }}>
            Хямдралд хамрах
          </div>
          <h2 className="text-[22px] sm:text-[28px] font-semibold tracking-[-0.02em] leading-none text-white" style={{ fontFamily: 'Gilroy, Inter, sans-serif' }}>
            {isLoading ? '...' : `${total} бүтээгдэхүүн`}
          </h2>
        </div>
        {!isLoading && total > 0 && (
          <div className="text-[13px]" style={{ color: 'rgba(255,255,255,0.55)' }}>
            {visibleCount} / {total} харагдаж байна
          </div>
        )}
      </div>

      {isLoading ? (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-x-4 gap-y-10">
          {Array.from({ length: skeletonCount }).map((_, i) => (
            <div key={i} className="flex flex-col gap-3">
              <div className="aspect-square w-full rounded-xl animate-pulse" style={{ background: 'rgba(255,255,255,0.06)' }} />
              <div className="h-4 w-3/4 rounded animate-pulse" style={{ background: 'rgba(255,255,255,0.06)' }} />
              <div className="h-3 w-1/2 rounded animate-pulse" style={{ background: 'rgba(255,255,255,0.06)' }} />
            </div>
          ))}
        </div>
      ) : products.length > 0 ? (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-x-4 gap-y-10">
            {products.map((product: any, index: number) => (
              <motion.div
                key={product.customId || product.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: Math.min(index * 0.05, 0.5), duration: 0.5, ease: 'easeOut' }}
                viewport={{ once: true }}
              >
                <ProductCard product={product} vendor={vendor} />
              </motion.div>
            ))}
          </div>
          {loadMore && (
            <div ref={loadMoreRef} className="w-full pt-12 flex justify-center">
              {isLoadingMore && (
                <div className="inline-flex items-center gap-2 text-[13px]" style={{ color: 'rgba(255,255,255,0.6)' }}>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span>Ачаалж байна...</span>
                </div>
              )}
              {!hasMore && products.length > 0 && (
                <div className="flex flex-col items-center gap-4">
                  <span className="text-[13px]" style={{ color: 'rgba(255,255,255,0.55)' }}>
                    Бүх бүтээгдэхүүн ачаалагдлаа ({total})
                  </span>
                  <button
                    type="button"
                    onClick={scrollToTop}
                    className="inline-flex items-center gap-2 h-10 px-5 rounded-full text-[13px] font-medium cursor-pointer transition-colors"
                    style={{ background: 'transparent', color: 'white', border: '1px solid rgba(255,255,255,0.2)' }}
                  >
                    <ArrowUp className="h-3.5 w-3.5" /> Дээш буцах
                  </button>
                </div>
              )}
            </div>
          )}
        </>
      ) : (
        <div
          className="rounded-2xl p-10 text-center"
          style={{ background: 'rgba(255,255,255,0.035)', border: '1px solid rgba(255,255,255,0.07)' }}
        >
          <div
            className="mx-auto w-12 h-12 rounded-full grid place-items-center mb-3"
            style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}
          >
            <PackageOpen className="w-5 h-5" style={{ color: 'rgba(255,255,255,0.5)' }} />
          </div>
          <div className="text-[14px] font-medium text-white mb-1">Бүтээгдэхүүн байхгүй байна</div>
          <div className="text-[12.5px]" style={{ color: 'rgba(255,255,255,0.55)' }}>
            Энэ урамшуулалд хамаарах бүтээгдэхүүн байхгүй байна.
          </div>
        </div>
      )}
    </motion.div>
  )
}

function InfiniteCampaignGrid({ vendorId, vendor }: { vendorId: string, vendor: any }) {
  const { products, isLoading, isLoadingMore, metadata, loadMore, hasMore } = useInfiniteProducts({ vendorId, limit: 20 })
  return (
    <CampaignProductGrid
      products={products}
      isLoading={isLoading}
      isLoadingMore={isLoadingMore}
      total={metadata.total}
      hasMore={hasMore}
      loadMore={loadMore}
      vendor={vendor}
      skeletonCount={10}
    />
  )
}

function CategoryCampaignGrid({ vendorId, categoryIds, vendor }: { vendorId: string, categoryIds: string[], vendor: any }) {
  const { expandedIds, loaded } = useCategoryExpander(vendorId, categoryIds)
  if (!loaded) {
    return (
      <CampaignProductGrid
        products={[]} isLoading total={0}
        vendor={vendor} skeletonCount={10}
      />
    )
  }
  return <CategoryCampaignGridLoaded vendorId={vendorId} expandedIds={expandedIds} vendor={vendor} />
}

function CategoryCampaignGridLoaded({ vendorId, expandedIds, vendor }: { vendorId: string, expandedIds: string, vendor: any }) {
  const { products, isLoading, isLoadingMore, metadata, loadMore, hasMore } = useInfiniteProducts({
    vendorId, limit: 20, initialFilters: { category: expandedIds },
  })
  return (
    <CampaignProductGrid
      products={products}
      isLoading={isLoading}
      isLoadingMore={isLoadingMore}
      total={metadata.total}
      hasMore={hasMore}
      loadMore={loadMore}
      vendor={vendor}
      skeletonCount={10}
    />
  )
}

function ProductIdsCampaignGrid({ vendorId, productIds, vendor }: { vendorId: string, productIds: string[], vendor: any }) {
  const { products, isLoading } = useProductsByIds(vendorId, productIds)
  return (
    <CampaignProductGrid
      products={products}
      isLoading={isLoading}
      total={products.length}
      vendor={vendor}
      skeletonCount={productIds.length}
    />
  )
}
